
export const handlePaste = (e, editor) => {
  e.preventDefault();

  const clipboardData = e.clipboardData;
  if (!clipboardData) return;

  // Save current selection
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);

  // Get HTML content from clipboard
  const html = clipboardData.getData("text/html");

  // If no HTML, get plain text
  if (!html) {
    const text = clipboardData.getData("text/plain");
    if (text) {
      // Insert plain text and set cursor after it
      const textNode = document.createTextNode(text);
      range.deleteContents();
      range.insertNode(textNode);

      // Set cursor after inserted text
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);

      // Take immediate snapshot BEFORE dispatching input event
      // This ensures the snapshot has the correct cursor position
      if (editor?.history) {
        editor.history.takeSnapshot();
      }

      // Trigger input event for onChange
      const target = e.target;
      if (target) {
        const inputEvent = new Event("input", {
          bubbles: true,
          cancelable: true,
        });
        target.dispatchEvent(inputEvent);
      }
    }
    return;
  }

  // Parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  // Remove images and videos
  body
    .querySelectorAll("img, video, audio, iframe")
    .forEach((el) => el.remove());

  // Normalize elements
  const normalizedHTML = normalizeElements(body);

  // Create a temporary container to parse normalized HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = normalizedHTML;

  // Delete current selection content
  range.deleteContents();

  // Insert all nodes from temp container
  const fragment = document.createDocumentFragment();
  const insertedNodes = [];

  while (tempDiv.firstChild) {
    const node = tempDiv.firstChild;
    insertedNodes.push(node);
    fragment.appendChild(node);
  }

  range.insertNode(fragment);

  // Set cursor after last inserted node
  if (insertedNodes.length > 0) {
    const lastNode = insertedNodes[insertedNodes.length - 1];

    // Find last text node in the inserted content
    const findLastTextNode = (node) => {
      if (!node) return null;

      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.trim() ? node : null;
      }

      // Traverse children in reverse order
      if (node.childNodes && node.childNodes.length > 0) {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          const result = findLastTextNode(node.childNodes[i]);
          if (result) return result;
        }
      }

      return null;
    };

    const lastTextNode = findLastTextNode(lastNode);

    if (lastTextNode) {
      // Set cursor at end of last text node
      const length = lastTextNode.textContent?.length || 0;
      try {
        const newRange = document.createRange();
        newRange.setStart(lastTextNode, length);
        newRange.setEnd(lastTextNode, length);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        try {
          const newRange = document.createRange();
          newRange.setStartAfter(lastNode);
          newRange.setEndAfter(lastNode);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } catch (e2) {
          return e2
        }
      }
    } else {
      // No text node found, set cursor after last node
      try {
        const newRange = document.createRange();
        newRange.setStartAfter(lastNode);
        newRange.setEndAfter(lastNode);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        return e;
      }
    }
  }

  // Take immediate snapshot BEFORE dispatching input event
  // This ensures the snapshot has the correct cursor position
  if (editor?.history) {
    editor.history.takeSnapshot();
  }

  // Trigger input event for onChange
  const target = e.target;
  if (target) {
    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
    target.dispatchEvent(inputEvent);
  }
};

const normalizeElements = (container) => {
  const allowedTags = [
    "ul",
    "ol",
    "li",
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
  ];
  const result = [];

  const processNode = (node) => {
    // Text node - return text content
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    // Element node
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node;
      const tagName = element.tagName.toLowerCase();

      // Process children first
      let childrenHTML = "";
      Array.from(element.childNodes).forEach((child) => {
        childrenHTML += processNode(child);
      });

      // If allowed tag, keep it
      if (allowedTags.includes(tagName)) {
        // For list items, wrap content properly
        if (tagName === "li") {
          return `<li>${childrenHTML}</li>`;
        }
        // For lists, keep structure
        if (tagName === "ul" || tagName === "ol") {
          return `<${tagName}>${childrenHTML}</${tagName}>`;
        }
        // For headings and paragraphs
        if (
          tagName.startsWith("h") ||
          tagName === "p" ||
          tagName === "blockquote"
        ) {
          return `<${tagName}>${childrenHTML}</${tagName}>`;
        }
      }

      // For br, convert to line break
      if (tagName === "br") {
        return "<br>";
      }

      // For div, span, and other inline elements, just return content
      if (
        tagName === "div" ||
        tagName === "span" ||
        tagName === "a" ||
        tagName === "strong" ||
        tagName === "em" ||
        tagName === "b" ||
        tagName === "i"
      ) {
        return childrenHTML;
      }

      // For block elements not in allowed list, wrap in <p>
      const blockElements = [
        "div",
        "section",
        "article",
        "header",
        "footer",
        "nav",
        "aside",
        "main",
      ];
      if (blockElements.includes(tagName)) {
        return childrenHTML ? `<p>${childrenHTML}</p>` : "";
      }

      // Default: return content without wrapper
      return childrenHTML;
    }

    return "";
  };

  // Process all children of container
  Array.from(container.childNodes).forEach((child) => {
    const processed = processNode(child);
    if (processed.trim()) {
      result.push(processed);
    }
  });

  return result.join("");
};
